import { BTCheckbox } from '@common-components/atoms/bt-checkbox/bt-checkbox'
import { TextField } from '@mui/material'
import { useAppSelector } from '@common-reducers/hooks/store.hook'
import { resolveWindowPrefill } from '@common-reducers/campaign-send-window-default'
import React, { useEffect } from 'react'

type Props = {
    handleTimeAndDateTabOnChangeEvent: (changeType: string, changeObject: any) => void;
}

const DEFAULT_WINDOW_MINUTES = 45;

// #max-send-window — opt-in "don't send late" cap. Enforcement is server-side; this only captures
// the setting. Card styling mirrors the Now/Schedule cards in bulk-set-time-and-date.tsx
// (bg-white dark:bg-slate-800 + slate text) — NOT the hardcoded-light bg of the hidden
// specific-hours dialog, which is invisible-white-on-white in dark mode (2026-07-04 fix).
export default function CampaignMaxSendWindowControl({
    handleTimeAndDateTabOnChangeEvent,
}: Props) {
    const newCampaign = useAppSelector(state => state.CampaignReducer.newCampaign)
    const windowMinutes = newCampaign?.timing?.maxSendWindowMinutes
    const userDefault = useAppSelector(state => state.UserReducer.user?.preferences?.defaultMaxSendWindowMinutes)

    // #max-send-window: pre-fill from the user's saved default (persisted at launch).
    // Key-presence guard: after an explicit uncheck the key EXISTS with value undefined
    // ('maxSendWindowMinutes' in timing), so a remount never re-applies the default over
    // the user's choice. Editing an existing campaign (_id set) keeps its own timing.
    const timingHasKey = !!newCampaign?.timing && 'maxSendWindowMinutes' in newCampaign.timing
    const isEditingExisting = !!newCampaign?._id
    useEffect(() => {
        const prefill = resolveWindowPrefill(timingHasKey, isEditingExisting, userDefault)
        if (prefill != null) {
            handleTimeAndDateTabOnChangeEvent('setNewCampaignTiming', { maxSendWindowMinutes: prefill })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return (
        <div className="flex flex-col gap-2 bg-white dark:bg-slate-800 border-2 border-slate-200 dark:border-slate-600 rounded-xl p-4">
            <div className="flex flex-row items-center">
                <BTCheckbox
                    onChange={(event) => {
                        handleTimeAndDateTabOnChangeEvent(
                            'setNewCampaignTiming',
                            { maxSendWindowMinutes: event.target.checked ? DEFAULT_WINDOW_MINUTES : undefined }
                        )
                    }}
                    checked={windowMinutes != null} />
                <span className="text-base font-semibold text-slate-900 dark:text-slate-50 ml-2">
                    Skip messages that miss their send time
                </span>
            </div>

            {windowMinutes != null && (
                <div className="flex flex-row items-center gap-2 pl-7.5">
                    <TextField
                        type="number"
                        size="small"
                        label="Minutes"
                        value={windowMinutes}
                        InputProps={{ inputProps: { min: 1 } }}
                        onChange={(event) => {
                            const v = Number(event.target.value);
                            handleTimeAndDateTabOnChangeEvent(
                                'setNewCampaignTiming',
                                { maxSendWindowMinutes: Number.isFinite(v) && v >= 1 ? Math.round(v) : 1 }
                            )
                        }}
                    />
                    <span className="text-sm text-slate-600 dark:text-slate-400">
                        If a message can&apos;t be sent within this many minutes of its scheduled time
                        (e.g. your connection was offline), it is skipped instead of sent late.
                    </span>
                </div>
            )}
        </div>
    )
}
